import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { faTag } from '@fortawesome/pro-light-svg-icons/faTag';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { AddToListMenu } from '../components/AddToListMenu';
import { useContactActions } from '../hooks/useContactActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import type { ContactRow } from '../outreachTypes';
import { outreachMessages } from '../outreachMessages';
import { contactsMessages as messages } from './contactsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ContactsBulkBarProps {
  accountId: number;
  rows: ContactRow[];
  onDone: () => void;
}

/**
 * The bulk bar of Contacts (design outreach q1): add to a list and tag. Outreach v2 has no "Pitch
 * these": Plug picks the people of a campaign every day (Task 14 rebuilds Contacts).
 */
const ContactsBulkBarComponent: React.FC<ContactsBulkBarProps> = ({ accountId, rows, onDone }) => {
  const intl = useIntl();
  const plan = useOutreachPlan();
  const { updateContact } = useContactActions(accountId);
  const withEmail = useMemo(() => rows.filter((row) => row.hasEmail && row.status !== 'doNotContact'), [rows]);
  const uuids = useMemo(() => rows.map((row) => row.workspaceContactUuid), [rows]);
  const resolveContacts = useCallback(() => Promise.resolve(uuids), [uuids]);

  const tag = useCallback(() => {
    // eslint-disable-next-line no-alert -- one short word
    const value = window.prompt(intl.formatMessage(messages.tagPrompt))?.trim() ?? '';
    if (value === '') {
      return;
    }
    Promise.all(
      rows.map((row) =>
        updateContact({ workspaceContactUuid: row.workspaceContactUuid, tags: [...new Set([...row.tags, value])] })
      )
    )
      .then(onDone)
      .catch(console.error);
  }, [intl, onDone, rows, updateContact]);

  return (
    <div className={styles.selBar} data-testid="contacts-bulk-bar">
      <span className={`${styles.mono} fw-semibold`}>
        {intl.formatMessage(outreachMessages.picked, { count: rows.length })}
      </span>
      <span className={`${styles.small} ${styles.grow}`}>
        {intl.formatMessage(messages.bulkMeta, {
          withEmail: withEmail.length,
          without: rows.length - withEmail.length,
        })}
      </span>
      <AddToListMenu accountId={accountId} resolveContacts={resolveContacts} onDone={onDone} disabled={plan.isDemo} />
      <SMLButton size="sm" outline omitCap leftIcon={faTag} onClick={tag} disabled={plan.isDemo}>
        {intl.formatMessage(messages.tag)}
      </SMLButton>
    </div>
  );
};

export const ContactsBulkBar = TypedMemo(ContactsBulkBarComponent);
